// The Connections view — data feeds, API keys, notifications, account, and
// (soon) the Pi trading server. Rendered as an in-page overlay ("popup box")
// on the current surface rather than a separate browser tab. Returns a
// teardown that drops its store subscriptions when the box is closed.

import { el, spacer, divider, closeButton } from "./dom.js";
import { storageGet, storageSet } from "./storage.js";
import { allProviders, selectedProviderId, setSelectedProviderId } from "./data-providers/registry.js";
import { getStoredLicense, licenseGateEnabled, signOut, billingPortalUrl, login } from "./license.js";
import { fetchServerStatus, createPairingCode, unpairServer, setSecret, deployStrategy, deployCalendar, deployNews, deployEarnings, sendControl, setMode, isOnline, agoLabel, doAuthorizeUrl, doStatus, doDisconnect, doProvision, doRegions, type ServerStatus, type DoStatus } from "./trading-server.js";
import { strategyStore } from "./strategy.js";
import { loadStrategy, listSavedStrategies, loadNamedStrategy } from "./composable/storage.js";
import type { Strategy } from "./composable/model.js";

// Keys pushed to the box (sealed). Field names match what the engine reads
// from secrets.json (Trading, 2026-08-09): Topstep/ProjectX auth = username +
// API key + account id (no "secret"; account id is required to know which
// account to trade). Broker-specific sets come with the per-broker connection
// UI later.
const SECRET_FIELDS: Array<{ name: string; label: string }> = [
  { name: "broker_username", label: "Broker username" },
  { name: "broker_api_key", label: "Broker API key" },
  { name: "account_id", label: "Account ID" },
  { name: "symbol", label: "Symbol (optional)" },
];


/** Renders the Connections content into `root`. Returns a teardown that
 * removes its store subscriptions. Async because it initialises the stores. */
export async function renderConnections(root: HTMLElement, onClose: () => void): Promise<() => void> {
  await strategyStore.init();
  const ui = { providerStatus: {} as Record<string, string> };

  // --- Trading server (D-056, milestone 1: pair + live status) -------------
  // A persistent body we repopulate on a poll, so the status stays fresh
  // without rebuilding the whole page.
  let pairing: { code: string; expiresAt: number } | null = null;
  // Once the sign-in form is on screen, the status poll must NOT rebuild it —
  // that would wipe what the user is typing and steal focus.
  let showingSignIn = false;
  // While a key field is being edited, the status poll must not rebuild the
  // panel (it would wipe what's being typed).
  let keysEditing = false;
  // Broker creds the user already entered in the Data feeds section (so they
  // aren't typed twice) — mapped from the matching data-feed provider.
  let reuseCreds: { broker_username?: string; broker_api_key?: string } | null = null;
  // DigitalOcean "Connect your cloud" (D-057). Retail-only path — hidden until
  // the backend has DO app credentials (configured=false ⇒ don't render it).
  let doConn: DoStatus | null = null;

  async function loadReuseCreds(): Promise<void> {
    try {
      const ts = allProviders.find((p) => p.id === "topstepx");
      const c = ts?.byo ? await ts.byo.getCreds() : null;
      reuseCreds = c && (c.userName || c.apiKey) ? { broker_username: c.userName, broker_api_key: c.apiKey } : null;
    } catch {
      reuseCreds = null;
    }
  }
  const serverBody = el("div", { style: { display: "flex", flexDirection: "column", gap: "8px" } });

  function statusLine(label: string, value: string): HTMLElement {
    return el("div", { style: { display: "flex", gap: "8px", font: "var(--font-caption)" } }, [
      el("div", { text: label, style: { color: "var(--text-secondary)", width: "96px", flex: "none" } }),
      el("div", { text: value, style: { color: "var(--text-primary)" } }),
    ]);
  }

  function keyToServerRow(field: { name: string; label: string }, publicKey: string, savedInitially: boolean): HTMLElement {
    let saved = savedInitially; // does the box hold this key?
    let editing = !saved; // an unsaved field starts editable
    let saving = false;
    const controlRow = el("div", { style: { display: "flex", gap: "6px", alignItems: "center", flexWrap: "wrap" } });

    function render(): void {
      controlRow.replaceChildren();

      if (saved && !editing) {
        // Saved state: a box that looks like the field, showing the green
        // confirmation, plus a Replace button.
        const box = el("div", {
          text: `${field.label} saved ✓`,
          style: {
            flex: "1",
            minWidth: "0",
            border: "1px solid var(--border)",
            borderRadius: "8px",
            padding: "6px 8px",
            color: "var(--up)",
            background: "var(--bg-fill)",
            overflow: "hidden",
            textOverflow: "ellipsis",
            whiteSpace: "nowrap",
          },
        });
        const replace = el("button", {
          className: "pill",
          text: "Replace",
          onClick: () => {
            editing = true;
            render();
          },
        });
        controlRow.append(box, replace);
        return;
      }

      // Editing state: real input + Save. Clicking away without saving reverts
      // to the saved box (when there was one).
      const input = el("input", {
        attrs: { type: "password", placeholder: `Enter ${saved ? "a new" : "your"} ${field.label}` },
        style: { flex: "1", minWidth: "0" },
      }) as HTMLInputElement;
      const msg = el("span", { style: { font: "var(--font-caption)", color: "var(--down)", whiteSpace: "nowrap" } });
      const save = el("button", {
        className: "pill",
        text: "Save",
        onClick: () => {
          const value = input.value.trim();
          if (!value) return;
          saving = true;
          keysEditing = false;
          save.textContent = "Saving…";
          (save as HTMLButtonElement).disabled = true;
          void setSecret(field.name, publicKey, value).then((ok) => {
            saving = false;
            if (ok) {
              saved = true;
              editing = false;
              render();
            } else {
              save.textContent = "Save";
              (save as HTMLButtonElement).disabled = false;
              msg.textContent = "Couldn’t save — try again";
            }
          });
        },
      });
      input.addEventListener("focus", () => (keysEditing = true));
      input.addEventListener("blur", () => {
        keysEditing = false;
        // Give a click on Save time to land before reverting.
        setTimeout(() => {
          if (saved && editing && !saving) {
            editing = false;
            render();
          }
        }, 150);
      });
      controlRow.append(input, save, msg);
      // If we entered edit mode by pressing Replace, focus the box.
      if (saved) setTimeout(() => input.focus(), 0);
    }

    render();
    return el("div", { style: { display: "flex", flexDirection: "column", gap: "3px" } }, [
      el("div", { text: field.label, style: { font: "var(--font-caption)", color: "var(--text-secondary)" } }),
      controlRow,
    ]);
  }

  function strategyRow(strat: Strategy, publicKey: string, isDeployed: boolean): HTMLElement {
    const status = el("span", { style: { font: "var(--font-caption)", color: "var(--up)", whiteSpace: "nowrap" }, text: isDeployed ? "Deployed ✓" : "" });
    const btn = el("button", {
      className: "pill",
      text: isDeployed ? "Redeploy" : "Deploy",
      onClick: () => {
        btn.textContent = isDeployed ? "Redeploying…" : "Deploying…";
        (btn as HTMLButtonElement).disabled = true;
        void deployStrategy(publicKey, strat).then((ok) => {
          btn.textContent = isDeployed ? "Redeploy" : "Deploy";
          (btn as HTMLButtonElement).disabled = false;
          status.textContent = ok ? "Sent ✓" : "Failed";
          status.style.color = ok ? "var(--up)" : "var(--down)";
          if (ok) { void deployCalendar(publicKey); void deployNews(publicKey); void deployEarnings(publicKey); } // keep the box's econ-calendar (D-065) + news (D-072) + earnings (D-091) feeds current
        });
      },
    });
    return el("div", { style: { display: "flex", gap: "6px", alignItems: "center" } }, [
      el("div", { text: strat.name, style: { font: "var(--font-body)", flex: "1", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" } }),
      status,
      btn,
    ]);
  }

  function serverNodes(status: ServerStatus | null): HTMLElement[] {
    if (!status) {
      return [el("div", { text: "Sign in to TickAhead to connect a trading server.", style: { font: "var(--font-caption)", color: "var(--text-secondary)" } })];
    }

    // Mid-pairing: guided setup — the code baked into a copy-paste one-liner.
    if (pairing && !status.paired && pairing.expiresAt * 1000 > Date.now()) {
      const mins = Math.max(1, Math.round((pairing.expiresAt * 1000 - Date.now()) / 60000));
      const cancel = el("button", { className: "pill", text: "Cancel", onClick: () => { pairing = null; void refreshServer(); } });
      const cmd = `curl -fsSL https://api.tickahead.com/v1/agent/install.sh | bash -s ${pairing.code}`;

      // Monospace command box with a copy button.
      const copyBtn = el("button", { className: "pill", text: "Copy", style: { flex: "none" } });
      copyBtn.addEventListener("click", () => {
        void navigator.clipboard.writeText(cmd).then(() => {
          copyBtn.textContent = "Copied ✓";
          setTimeout(() => (copyBtn.textContent = "Copy"), 1500);
        });
      });
      const cmdBox = el("div", { style: { display: "flex", gap: "6px", alignItems: "center" } }, [
        el("code", {
          text: cmd,
          style: { flex: "1", minWidth: "0", font: "var(--font-mono, monospace)", background: "var(--bg-fill)", border: "1px solid var(--border)", borderRadius: "8px", padding: "6px 8px", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" },
        }),
        copyBtn,
      ]);

      return [
        el("div", { text: "Set up your server", style: { font: "var(--font-header)" } }),
        el("div", {
          text: "On the always-on machine that will run your bot — your own computer or a Raspberry Pi — paste this into a terminal. Nothing to download first:",
          style: { font: "var(--font-caption)", color: "var(--text-secondary)" },
        }),
        cmdBox,
        el("div", {
          text: "It installs, pairs, and starts automatically (keep the machine awake and online). No SSH or setup — this box updates the moment it connects.",
          style: { font: "var(--font-caption)", color: "var(--text-secondary-faint)" },
        }),
        el("div", { text: `Pairing code: ${pairing.code} · expires in ${mins} min`, style: { font: "var(--font-caption)", color: "var(--text-secondary-faint)" } }),
        cancel,
      ];
    }

    // Paired: live status readout.
    if (status.paired) {
      const online = isOnline(status.lastSeen);
      const s = status.status ?? {};
      const header = el("div", { style: { display: "flex", alignItems: "center", gap: "8px" } }, [
        el("div", { text: status.name ?? "Trading server", style: { font: "var(--font-header)" } }),
        el("div", {
          text: online ? "● Online" : "○ Offline",
          style: { font: "var(--font-caption)", color: online ? "var(--up)" : "var(--text-secondary)" },
        }),
      ]);
      const lines: HTMLElement[] = [header];
      if (s.strategy) lines.push(statusLine("Strategy", String(s.strategy)));
      if (s.account) lines.push(statusLine("Account", String(s.account)));
      if (s.mode) lines.push(statusLine("Mode", String(s.mode)));
      if (s.state) lines.push(statusLine("State", String(s.state)));
      const positions = Array.isArray(s.openPositions) ? s.openPositions : [];
      const posCount = Array.isArray(s.openPositions) ? s.openPositions.length : typeof s.openPositions === "number" ? s.openPositions : undefined;
      if (posCount !== undefined) lines.push(statusLine("Open positions", String(posCount)));
      for (const p of positions) {
        const upnl = typeof p.uPnL === "number" ? ` (${p.uPnL >= 0 ? "+" : ""}${p.uPnL})` : "";
        lines.push(statusLine("", `${p.symbol} ${p.side} ${p.qty} @ ${p.entry}${upnl}`));
      }
      if (typeof s.pnlToday === "number") lines.push(statusLine("P&L today", `${s.pnlToday >= 0 ? "+" : ""}${s.pnlToday}`));
      lines.push(statusLine("Last heartbeat", agoLabel(status.lastSeen)));

      // Keys — sealed in the browser to this box's key and pushed over the
      // relay (which never sees plaintext). Shows "set" from the box's own
      // heartbeat, never the value.
      if (status.publicKey) {
        const pubKey = status.publicKey;
        const already = new Set(s.secrets_set ?? []);
        lines.push(el("div", { text: "Keys", style: { font: "var(--font-header)", marginTop: "8px" } }));
        lines.push(
          el("div", {
            text: "Keys are encrypted on your device and sent to your server — TickAhead never sees them. A green “Saved” means your server has it.",
            style: { font: "var(--font-caption)", color: "var(--text-secondary)" },
          })
        );
        // Reuse creds already entered under Data feeds (no double-typing).
        if (reuseCreds && (reuseCreds.broker_username || reuseCreds.broker_api_key)) {
          const creds = reuseCreds;
          const reuseBtn = el("button", { className: "pill", text: "Reuse for server" });
          reuseBtn.addEventListener("click", () => {
            reuseBtn.textContent = "Sending…";
            (reuseBtn as HTMLButtonElement).disabled = true;
            const jobs: Array<Promise<boolean>> = [];
            if (creds.broker_username) jobs.push(setSecret("broker_username", pubKey, creds.broker_username));
            if (creds.broker_api_key) jobs.push(setSecret("broker_api_key", pubKey, creds.broker_api_key));
            void Promise.all(jobs).then(() => refreshServer());
          });
          lines.push(
            el("div", { style: { display: "flex", gap: "6px", alignItems: "center", flexWrap: "wrap" } }, [
              el("div", { text: "You’ve already connected TopstepX under Data feeds — send that username + API key to your server too:", style: { font: "var(--font-caption)", color: "var(--text-secondary)", flex: "1", minWidth: "180px" } }),
              reuseBtn,
            ])
          );
        }
        for (const field of SECRET_FIELDS) {
          lines.push(keyToServerRow(field, pubKey, already.has(field.name)));
        }

        // Strategy — deploy a strategy you built and backtested. Sealed like a
        // key (the trader's own IP), so the relay never sees it.
        lines.push(el("div", { text: "Strategy", style: { font: "var(--font-header)", marginTop: "8px" } }));
        // Deployable = the composable strategies you built (named library + the
        // current working one), deduped by name. These are what compile → the
        // builder-graph envelope the box runs (D-064).
        const deployable: Strategy[] = [];
        const seenNames = new Set<string>();
        for (const name of listSavedStrategies()) {
          const st = loadNamedStrategy(name) as Strategy | null;
          if (st && st.name && !seenNames.has(st.name)) { deployable.push(st); seenNames.add(st.name); }
        }
        const working = loadStrategy() as Strategy | null;
        if (working && working.name && !seenNames.has(working.name)) { deployable.push(working); seenNames.add(working.name); }
        if (deployable.length) {
          for (const strat of deployable) {
            lines.push(strategyRow(strat, pubKey, s.strategy === strat.name));
          }
        } else {
          lines.push(
            el("div", {
              text: "Build and backtest a strategy in the Strategy Lab, then deploy it here.",
              style: { font: "var(--font-caption)", color: "var(--text-secondary)" },
            })
          );
        }

        // Controls — start/stop/flatten and paper↔live. These act on the box's
        // engine (which honours them); the risky ones confirm first.
        lines.push(el("div", { text: "Controls", style: { font: "var(--font-header)", marginTop: "8px" } }));
        const controlBtn = (label: string, run: () => Promise<boolean>, confirmMsg?: string) => {
          const b = el("button", { className: "pill", text: label });
          b.addEventListener("click", () => {
            if (confirmMsg && !window.confirm(confirmMsg)) return;
            const prev = b.textContent;
            b.textContent = "…";
            (b as HTMLButtonElement).disabled = true;
            void run().then((ok) => {
              (b as HTMLButtonElement).disabled = false;
              b.textContent = ok ? `${prev} ✓` : `${prev} ✕`;
              setTimeout(() => (b.textContent = prev), 1500);
            });
          });
          return b;
        };
        const live = s.mode === "live";
        lines.push(
          el("div", { style: { display: "flex", gap: "6px", flexWrap: "wrap", alignItems: "center" } }, [
            controlBtn("Start", () => sendControl("start")),
            controlBtn("Stop", () => sendControl("stop")),
            controlBtn("Flatten", () => sendControl("flatten"), "Close all open positions now and stop?"),
          ])
        );
        lines.push(
          el("div", {
            text: "Start = take new trades · Stop = pause new entries (open trades stay managed/protected) · Flatten = close everything now.",
            style: { font: "var(--font-caption)", color: "var(--text-secondary-faint)" },
          })
        );
        lines.push(
          el("div", { style: { display: "flex", gap: "6px", alignItems: "center", marginTop: "2px" } }, [
            el("div", { text: `Mode: ${live ? "Live" : "Paper (dry-run)"}`, style: { font: "var(--font-caption)", color: live ? "var(--down)" : "var(--text-secondary)", flex: "1" } }),
            live
              ? controlBtn("Switch to paper", () => setMode("dry-run"))
              : controlBtn("Go live", () => setMode("live"), "Request LIVE trading with real money? Your server's safety rails still apply (live only if enabled there)."),
          ])
        );
        lines.push(
          el("div", {
            text: "“Go live” is a request — your server keeps its own safety rails (dry-run by default; live only if you’ve enabled it there). Which account trades is set by the Account ID above.",
            style: { font: "var(--font-caption)", color: "var(--text-secondary-faint)" },
          })
        );
      }

      lines.push(
        el("div", { style: { display: "flex", gap: "6px", marginTop: "2px" } }, [
          el("button", {
            className: "pill",
            text: "Unpair",
            onClick: () => {
              if (window.confirm("Unpair this trading server? It'll stop reporting here until you pair it again.")) {
                void unpairServer().then(() => refreshServer());
              }
            },
          }),
        ])
      );
      lines.push(...cloudNodes(true));
      return lines;
    }

    // Not paired yet.
    return [
      el("div", {
        text: "Pair your always-on trading server (a Raspberry Pi or VPS running the TickAhead agent) to see its status here and deploy strategies to it. Nothing is exposed to the internet — the server dials out to TickAhead.",
        style: { font: "var(--font-caption)", color: "var(--text-secondary)" },
      }),
      el("button", {
        className: "pill",
        text: "Pair a server",
        onClick: () => {
          void createPairingCode().then((r) => {
            if ("error" in r) {
              serverBody.replaceChildren(el("div", { text: r.error, style: { font: "var(--font-caption)", color: "var(--down)" } }));
            } else {
              pairing = r;
              void refreshServer();
            }
          });
        },
      }),
      ...cloudNodes(false),
    ];
  }

  // "Connect your cloud" — DigitalOcean. Retail-only: hosting a prop-firm bot
  // on a VPS breaks their rules (Topstep etc. ban it), so we lead with the
  // warning and never help evade detection (D-057 non-negotiable). Hidden
  // entirely until the backend has DO credentials configured.
  function cloudNodes(paired: boolean): HTMLElement[] {
    if (!doConn?.configured) return [];
    const nodes: HTMLElement[] = [
      divider(),
      el("div", { text: paired ? "Run a server in the cloud" : "No spare machine? Connect your cloud", style: { font: "var(--font-header)" } }),
      el("div", {
        text: "Connect your own DigitalOcean account and we'll spin up a small always-on server for your bot — no hardware needed. Only for retail brokers: prop firms (Topstep and similar) forbid running on a VPS, so use your own computer or a Raspberry Pi for those.",
        style: { font: "var(--font-caption)", color: "var(--text-secondary)" },
      }),
    ];
    if (doConn.connected) {
      const provisionMsg = el("div", { style: { font: "var(--font-caption)", color: "var(--text-secondary)" } });
      // Region picker — populated from the user's DO account (only regions where
      // the smallest droplet is available). Pick one near you for lower latency.
      const regionSelect = el("select", { style: { width: "100%", padding: "6px 8px", borderRadius: "8px", border: "1px solid var(--border)", background: "var(--bg-fill)" } }) as HTMLSelectElement;
      regionSelect.appendChild(el("option", { text: "Loading regions…", attrs: { value: "" } }));
      void doRegions().then((regs) => {
        regionSelect.replaceChildren();
        if (!regs || regs.length === 0) {
          regionSelect.appendChild(el("option", { text: "Default region", attrs: { value: "" } }));
          return;
        }
        for (const r of regs) regionSelect.appendChild(el("option", { text: `${r.name} (${r.slug})`, attrs: { value: r.slug } }));
        // Sensible default: London, then New York, else the first available.
        const pref = ["lon1", "nyc1", "nyc3"].find((p) => regs.some((r) => r.slug === p));
        if (pref) regionSelect.value = pref;
      });
      const provisionBtn = el("button", { className: "pill", text: "Create my server ($6/mo on DigitalOcean)" });
      provisionBtn.addEventListener("click", () => {
        // One server per account — provisioning a cloud box replaces the current pairing.
        const warn = paired ? "\n\nThis will become your trading server and unpair the one you have now." : "";
        if (!window.confirm(`Create a new server on your DigitalOcean account? It costs about $6/month, billed by DigitalOcean. It'll set itself up and appear here as Online in a few minutes.${warn}`)) return;
        (provisionBtn as HTMLButtonElement).disabled = true;
        provisionBtn.textContent = "Creating…";
        void doProvision(regionSelect.value || undefined).then((r) => {
          if (r && "ok" in r) {
            provisionMsg.style.color = "var(--up)";
            provisionMsg.textContent = "Server is being set up — it'll appear as Online here in a few minutes. Then add your keys and deploy a strategy.";
            provisionBtn.remove();
          } else {
            (provisionBtn as HTMLButtonElement).disabled = false;
            provisionBtn.textContent = "Create my server ($6/mo on DigitalOcean)";
            provisionMsg.style.color = "var(--down)";
            provisionMsg.textContent = r ? ("error" in r ? r.error : "") : "Sign in first.";
            if (r && "reconnect" in r && r.reconnect) void refreshServer();
          }
        });
      });
      nodes.push(
        el("div", { text: "● DigitalOcean connected", style: { font: "var(--font-caption)", color: "var(--up)" } }),
        el("div", { text: "Region", style: { font: "var(--font-caption)", color: "var(--text-secondary)", marginTop: "4px" } }),
        regionSelect,
        el("div", { text: "Pick a region near you (and near your broker) for lower latency.", style: { font: "var(--font-caption)", color: "var(--text-secondary-faint)" } }),
        provisionBtn,
        provisionMsg,
        el("button", {
          className: "pill",
          text: "Disconnect DigitalOcean",
          onClick: () => {
            void doDisconnect().then(() => refreshServer());
          },
        })
      );
    } else {
      const btn = el("button", { className: "pill", text: "Connect DigitalOcean" });
      const msg = el("div", { style: { font: "var(--font-caption)", color: "var(--down)" } });
      btn.addEventListener("click", () => {
        (btn as HTMLButtonElement).disabled = true;
        btn.textContent = "Opening…";
        void doAuthorizeUrl().then((r) => {
          (btn as HTMLButtonElement).disabled = false;
          btn.textContent = "Connect DigitalOcean";
          if (!r) {
            msg.textContent = "Sign in first.";
          } else if ("error" in r) {
            msg.textContent = r.error;
          } else {
            window.open(r.url, "_blank", "noopener");
            msg.textContent = "Approve access in the new tab, then come back.";
            msg.style.color = "var(--text-secondary)";
          }
        });
      });
      nodes.push(el("div", { style: { display: "flex", gap: "6px", alignItems: "center" } }, [btn, msg]));
      nodes.push(
        el("div", {
          text: "DigitalOcean charges for the server (about $6/mo). You can disconnect any time.",
          style: { font: "var(--font-caption)", color: "var(--text-secondary-faint)" },
        })
      );
    }
    return nodes;
  }

  function signInNodes(): HTMLElement[] {
    const email = el("input", { attrs: { type: "email", placeholder: "Email" } }) as HTMLInputElement;
    const key = el("input", { attrs: { type: "text", placeholder: "License key (TA-…)" } }) as HTMLInputElement;
    const msg = el("div", { style: { font: "var(--font-caption)", color: "var(--down)" } });
    const btn = el("button", { className: "pill", text: "Sign in" });
    btn.addEventListener("click", () => {
      msg.textContent = "";
      btn.textContent = "Signing in…";
      (btn as HTMLButtonElement).disabled = true;
      void login(email.value, key.value).then((r) => {
        if (r.ok) {
          void refreshServer();
        } else {
          msg.textContent = r.message ?? "Sign in failed.";
          btn.textContent = "Sign in";
          (btn as HTMLButtonElement).disabled = false;
        }
      });
    });
    return [
      el("div", { text: "Sign in with your TickAhead account to connect a trading server.", style: { font: "var(--font-caption)", color: "var(--text-secondary)" } }),
      email,
      key,
      el("div", { style: { display: "flex", gap: "6px", alignItems: "center" } }, [btn, msg]),
    ];
  }

  async function refreshServer(): Promise<void> {
    if (keysEditing) return; // don't rebuild the panel mid-edit
    const license = await getStoredLicense();
    if (!license?.token) {
      // Mount the sign-in form once; leave it untouched on later polls so the
      // fields keep what the user is typing.
      if (!showingSignIn) {
        serverBody.replaceChildren(...signInNodes());
        showingSignIn = true;
      }
      return;
    }
    showingSignIn = false;
    const [status, do_] = await Promise.all([fetchServerStatus(), doStatus(), loadReuseCreds()]);
    if (do_) doConn = do_;
    if (status?.paired) pairing = null;
    serverBody.replaceChildren(...serverNodes(status));
  }

  // Tabbed shell matching the Watch settings overlay (title + pill tabs + ✕),
  // then a scrolling body showing the active tab (D-123). Each of the parts that
  // used to stack on one page now lives on its own tab.
  // Discord (customer-facing) is retired — D-131; Discord is internal-only now.
  type TabId = "account" | "feeds" | "keys" | "server";
  const TABS: Array<[TabId, string]> = [
    ["account", "Account"], ["feeds", "Data Feeds"], ["keys", "API Keys"], ["server", "Trading Server"],
  ];
  let tab: TabId = "account";

  const tabsRow = el("div", { style: { display: "inline-flex", gap: "2px", background: "var(--bg-fill)", padding: "3px", borderRadius: "999px", flexWrap: "wrap" } });
  const topBar = el("div", { style: { display: "flex", alignItems: "center", gap: "14px", padding: "11px 18px", background: "var(--bg)", borderBottom: "1px solid var(--border)", flex: "none" } }, [
    el("div", { text: "Connections", style: { fontWeight: "700", letterSpacing: "-.01em", flex: "none", fontSize: "16px" } }),
    tabsRow,
    el("div", { style: { flex: "1" } }),
  ]);
  const closeX = closeButton(onClose, { size: 16 });
  closeX.style.flex = "none";
  topBar.appendChild(closeX);
  const bodyInner = el("div", { style: { maxWidth: "680px", width: "100%", margin: "0 auto", display: "flex", flexDirection: "column", gap: "12px" } });
  const bodyWrap = el("div", { className: "scroll", style: { flex: "1", minHeight: "0", overflowY: "auto", padding: "18px" } }, [bodyInner]);
  root.replaceChildren(topBar, bodyWrap);

  function renderTabs(): void {
    tabsRow.replaceChildren(...TABS.map(([id, label]) => {
      const on = tab === id;
      const b = el("button", { text: label, style: { background: on ? "var(--bg)" : "transparent", color: on ? "var(--text-primary)" : "var(--text-secondary)", font: "inherit", fontWeight: on ? "600" : "500", padding: "6px 13px", borderRadius: "999px", cursor: "pointer", boxShadow: on ? "0 1px 2px rgba(0,0,0,.10)" : "none", whiteSpace: "nowrap" } });
      b.addEventListener("click", () => { tab = id; renderTabs(); renderBody(); });
      return b;
    }));
  }

  // Account — identity + subscription + the desktop-notifications master switch
  // (there's no separate Notifications tab, so it lives here).
  function buildAccount(host: HTMLElement): void {
    const info = el("div", { style: { display: "flex", flexDirection: "column", alignItems: "center", gap: "12px" } });
    host.appendChild(info);
    void Promise.all([licenseGateEnabled(), getStoredLicense()]).then(([, license]) => {
      info.replaceChildren(
        el("div", { text: license ? `Signed in as ${license.email}` : "Not signed in. Sign in from the Trading Server tab.", style: { font: "var(--font-body)", color: "var(--text-secondary)" } }),
      );
      if (license) {
        info.appendChild(el("div", { style: { display: "flex", gap: "8px", flexWrap: "wrap", justifyContent: "center" } }, [
          el("button", { className: "pill", text: "Manage subscription", onClick: () => { void billingPortalUrl().then((url) => window.open(url || "https://tickahead.com", "_blank", "noopener")); } }),
          el("button", { className: "pill", text: "Sign out", onClick: () => { if (window.confirm("Sign out of TickAhead? Your data stays on this machine.")) void signOut().then(() => location.reload()); } }),
        ]));
      }
    });
  }

  // API keys — the optional free Finnhub key (earnings + ex-dividend dates).
  function buildKeys(host: HTMLElement): void {
    host.appendChild(el("div", { text: "Earnings data (Finnhub)", style: { font: "var(--font-header)" } }));
    const finnBody = el("div", { style: { display: "flex", flexDirection: "column", gap: "6px" } });
    host.appendChild(finnBody);
    void storageGet<string>("finnhubKey", "").then((savedKey) => {
      const input = el("input", { attrs: { type: "password", placeholder: savedKey ? "Key saved — enter a new one to replace" : "Finnhub API key" }, style: { flex: "1", minWidth: "0" } }) as HTMLInputElement;
      const msg = el("span", { style: { font: "var(--font-caption)", color: "var(--text-secondary)", whiteSpace: "nowrap" } });
      const save = el("button", { className: "pill", text: "Save", onClick: () => { const value = input.value.trim(); void storageSet("finnhubKey", value).then(() => { msg.textContent = value ? "Saved ✓" : "Cleared"; msg.style.color = "var(--up)"; input.value = ""; }); } });
      finnBody.replaceChildren(
        el("div", { style: { display: "flex", gap: "6px", alignItems: "center" } }, [input, save, msg]),
        el("div", { text: "Free key from finnhub.io (no card). Powers earnings + ex-dividend dates on the chart and in strategy rules. Stored on this device only.", style: { font: "var(--font-caption)", color: "var(--text-secondary)" } }),
      );
    });
  }

  // Trading server — the pair + control panel; serverBody has its own status poll.
  function buildServer(host: HTMLElement): void {
    host.appendChild(serverBody);
    void refreshServer();
  }

  // Data feeds — free public data by default; connect your own live feed on top.
  function buildFeeds(host: HTMLElement): void {
    const dataSection = host;
    const byoProviders = allProviders.filter((p) => p.byo);
    void Promise.all([selectedProviderId(), ...byoProviders.map((p) => p.byo!.getCreds())]).then((results) => {
      const selected = results[0] as string;
      const credsById = new Map(byoProviders.map((p, i) => [p.id, results[i + 1] as Record<string, string> | null]));
      const activeLive = byoProviders.find((p) => p.id === selected && credsById.get(p.id));

      dataSection.appendChild(
        el("div", {
          text: activeLive
            ? `${activeLive.assetClasses.join("/")}: live via ${activeLive.displayName}. Everything else: free public data (delayed; crypto is real-time).`
            : "Free public data — delayed, not guaranteed live (crypto is real-time). Connect your own feed below for live data.",
          style: { font: "var(--font-caption)", color: "var(--text-secondary)" },
        })
      );

      for (const provider of byoProviders) {
        const creds = credsById.get(provider.id) ?? null;
        const isSelected = selected === provider.id && creds != null;

        dataSection.appendChild(
          el("div", { style: { display: "flex", alignItems: "center", gap: "6px" } }, [
            el("div", { text: provider.displayName, style: { font: "var(--font-header)" } }),
            el("div", { text: provider.assetClasses.join(" · "), style: { font: "var(--font-caption)", color: "var(--text-secondary-faint)" } }),
            spacer(),
            el("div", {
              text: isSelected ? "In use" : creds ? "Connected" : "Not connected",
              style: { font: "var(--font-caption)", color: isSelected ? "var(--up)" : "var(--text-secondary)" },
            }),
          ])
        );

        if (creds) {
          const row = el("div", { style: { display: "flex", gap: "6px", alignItems: "center" } });
          if (!isSelected) {
            row.appendChild(el("button", { className: "pill", text: "Use", onClick: () => void setSelectedProviderId(provider.id).then(render) }));
          }
          row.appendChild(el("button", { className: "pill", text: "Disconnect", onClick: () => { void provider.byo!.setCreds(null).then(() => (isSelected ? setSelectedProviderId("free") : Promise.resolve())).then(render); } }));
          dataSection.appendChild(row);
        } else {
          dataSection.appendChild(
            el("div", { text: `${provider.setup?.costNote ?? ""}. Credentials stay in this browser and talk only to ${provider.displayName.split(" ")[0]}.`, style: { font: "var(--font-caption)", color: "var(--text-secondary-faint)" } })
          );
          const inputs = provider.byo!.fields.map((field) => {
            const input = el("input", { attrs: { type: field.type, placeholder: field.label } }) as HTMLInputElement;
            dataSection.appendChild(input);
            return { field, input };
          });
          const connectBtn = el("button", { className: "pill", text: "Connect" });
          connectBtn.addEventListener("click", () => {
            const entered: Record<string, string> = {};
            for (const { field, input } of inputs) entered[field.key] = input.value.trim();
            if (Object.values(entered).some((v) => !v)) { ui.providerStatus[provider.id] = "Fill in every field."; render(); return; }
            connectBtn.textContent = "Connecting…";
            (connectBtn as HTMLButtonElement).disabled = true;
            void provider.byo!.verify(entered).then((result) => {
              if (result.ok) {
                void provider.byo!.setCreds(entered).then(() => setSelectedProviderId(provider.id)).then(() => { delete ui.providerStatus[provider.id]; render(); });
              } else { ui.providerStatus[provider.id] = result.message ?? "Connection failed."; render(); }
            });
          });
          const connectRow = el("div", { style: { display: "flex", gap: "6px", alignItems: "center" } }, [connectBtn]);
          if (provider.setup) {
            const docsLink = el("a", { text: "Get access", attrs: { href: provider.setup.signupUrl, target: "_blank", rel: "noopener" } });
            Object.assign(docsLink.style, { font: "var(--font-caption)", color: "var(--accent-blue)" });
            connectRow.appendChild(docsLink);
          }
          dataSection.appendChild(connectRow);
          if (ui.providerStatus[provider.id]) {
            dataSection.appendChild(el("div", { text: ui.providerStatus[provider.id]!, style: { font: "var(--font-caption)", color: "var(--down)" } }));
          }
        }
      }
    });
  }

  function renderBody(): void {
    bodyInner.replaceChildren();
    // Account is a sparse status panel — centre it in the empty space; every other
    // tab is a form and stays top-aligned.
    const centred = tab === "account";
    bodyInner.style.justifyContent = centred ? "center" : "flex-start";
    bodyInner.style.alignItems = centred ? "center" : "stretch";
    bodyInner.style.minHeight = centred ? "100%" : "0";
    if (tab === "account") buildAccount(bodyInner);
    else if (tab === "feeds") buildFeeds(bodyInner);
    else if (tab === "keys") buildKeys(bodyInner);
    else buildServer(bodyInner);
  }
  // Provider actions and notification changes re-render the ACTIVE tab only.
  function render(): void { renderBody(); }

  function keyField(label: string, value: string, onChange: (v: string) => void): HTMLElement {
    const input = el("input", { attrs: { type: "password", placeholder: "API key" } });
    input.value = value;
    input.addEventListener("change", () => onChange(input.value.trim()));
    return el("div", { style: { display: "flex", flexDirection: "column", gap: "3px" } }, [
      el("div", { text: label, style: { font: "var(--font-caption)", color: "var(--text-secondary)" } }),
      input,
    ]);
  }

  renderTabs();
  render();
  // Keep the trading-server status fresh while the box is open.
  const serverPoll = setInterval(() => void refreshServer(), 10_000);
  return () => {
    clearInterval(serverPoll);
  };
}
